import { Firestore, doc, updateDoc, collection, deleteField, deleteDoc } from 'firebase/firestore';
import { Functions } from 'firebase/functions';

import { FirebaseNovelSharing } from './novel-sharing';
import { FirebaseNovelSubscriptions } from './novel-subscriptions';
import { synchronizePublishedNovel } from './publication-sync';

import type { NovelStore, CreateNovelParams } from '../contracts/novel';
import type { Novel } from '@/lib/types';
import type { Novel as PersistedNovel } from '@common/schemas/novel.schema';

import { addDocumentNonBlocking } from '@/firebase/non-blocking-updates';
import { getThemeDefaultCategories } from '@/lib/theme-utils';

export class FirebaseNovelStore implements NovelStore {
  private sharing: FirebaseNovelSharing;
  private subscriptions: FirebaseNovelSubscriptions;

  constructor(private firestore: Firestore, private functions: Functions) {
    this.sharing = new FirebaseNovelSharing(functions);
    this.subscriptions = new FirebaseNovelSubscriptions(firestore);
  }

  subscribeNovel(
    ownerId: string,
    novelId: string,
    onUpdate: (novel: Novel | null) => void,
    onError: (error: Error) => void
  ): () => void {
    return this.subscriptions.subscribeNovel(ownerId, novelId, onUpdate, onError);
  }

  subscribeNovels(
    ownerId: string,
    onUpdate: (novels: Novel[]) => void,
    onError: (error: Error) => void
  ): () => void {
    return this.subscriptions.subscribeNovels(ownerId, onUpdate, onError);
  }

  async createNovel(params: CreateNovelParams): Promise<string | null> {
    const defaultCategories = getThemeDefaultCategories(params.theme);
    const defaultSettings: PersistedNovel['entitiesMeta']['settings'] = {};
    Object.keys(defaultCategories).forEach(categoryId => {
      defaultSettings[`codex:${categoryId}`] = [];
    });

    const newNovel: Omit<PersistedNovel, 'id'> = {
      title: params.title,
      ownerId: params.ownerId,
      status: 'draft',
      collaborators: {},
      readers: {},
      language: params.language,
      theme: params.theme,
      audience: params.audience,
      worldSetting: params.worldSetting,
      size: 'small',
      storageLayout: {
        entities: {
          characters: { items: 'embedded', meta: 'embedded' },
          locations: { items: 'embedded', meta: 'embedded' },
          organizations: { items: 'embedded', meta: 'embedded' },
          events: { items: 'embedded', meta: 'embedded' },
          codex: { items: 'embedded', meta: 'embedded' },
          relationships: { items: 'embedded', meta: 'embedded' },
        },
        narrative: {
          scenes: { items: 'embedded', meta: 'embedded' },
          chapters: { items: 'embedded', meta: 'embedded' },
          contents: { items: 'split', meta: 'embedded' },
          arcs: { items: 'embedded', meta: 'embedded' },
          storyboards: { items: 'embedded', meta: 'embedded' },
        }
      },
      ...(params.canCreateAIImages ? { imageStyle: 'style1' } : {}),
      canonRevision: '1',
      entitiesMeta: { summaries: {}, settings: defaultSettings, codexCategories: defaultCategories, codexCategoryParents: {} },
      narrativeMeta: {
        summaries: {
          contents: {}
        }
      },
      aiSettings: params.aiSettings || { intelligenceTier: 'eco' },
      noteFolders: {},
      noteFolderParents: {},
    };

    const collRef = collection(this.firestore, 'users', params.ownerId, 'novels');
    const novelRef = await addDocumentNonBlocking(collRef, newNovel);

    return novelRef ? novelRef.id : null;
  }

  async updateNovel(ownerId: string, novelId: string, updates: Partial<Novel>): Promise<void> {
    const docRef = doc(this.firestore, 'users', ownerId, 'novels', novelId);
    const mappedUpdates: Record<string, unknown> = {};
    Object.entries(updates).forEach(([key, val]) => {
      mappedUpdates[key] = val === undefined ? deleteField() : val;
    });
    await updateDoc(docRef, mappedUpdates);
    await synchronizePublishedNovel(this.functions, ownerId, novelId);
  }

  async synchronizePublication(ownerId: string, novelId: string): Promise<void> {
    await synchronizePublishedNovel(this.functions, ownerId, novelId);
  }
  async deleteNovel(ownerId: string, novelId: string): Promise<void> {
    const docRef = doc(this.firestore, 'users', ownerId, 'novels', novelId);
    await deleteDoc(docRef);
  }

  resolveUserByQuery(query: string) {
    return this.sharing.resolveUserByQuery(query);
  }

  sendEmailInvitation(params: {
    email: string;
    novelId: string;
    ownerId: string;
    role: 'collaborator' | 'reader';
    novelTitle: string;
  }) {
    return this.sharing.sendEmailInvitation(params);
  }

  addNovelMember(params: {
    novelId: string;
    memberUid: string;
    role: 'collaborator' | 'reader';
  }) {
    return this.sharing.addNovelMember(params);
  }

  removeNovelMember(params: {
    ownerId: string;
    novelId: string;
    memberUid?: string;
    email?: string;
    role: 'collaborator' | 'reader';
  }) {
    return this.sharing.removeNovelMember(params);
  }

  getInvitationDetails(token: string) {
    return this.sharing.getInvitationDetails(token);
  }

  acceptInvitation(token: string) {
    return this.sharing.acceptInvitation(token);
  }

  declineInvitation(token: string) {
    return this.sharing.declineInvitation(token);
  }
}
